import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Info } from 'lucide-react';
import logo from './gradegoal-logo.png';

export default function GradeGoal() {
  const emptyAssessment = () => ({ name: '', weight: '50', score: '' });
  const emptyModule = () => ({ 
    name: '', 
    credits: '20',  // Changed from weight to credits, default 20
    assessments: [emptyAssessment(), emptyAssessment()]
  });

  const [targetScore, setTargetScore] = useState('70');
  const [year1Weight, setYear1Weight] = useState('0');
  const [year2Weight, setYear2Weight] = useState('40');
  const [year3Weight, setYear3Weight] = useState('60');
  
  const [year1sem1Weight, setYear1Sem1Weight] = useState('50');
  const [year1sem2Weight, setYear1Sem2Weight] = useState('50');
  const [year2sem1Weight, setYear2Sem1Weight] = useState('50');
  const [year2sem2Weight, setYear2Sem2Weight] = useState('50');
  const [year3sem1Weight, setYear3Sem1Weight] = useState('50');
  const [year3sem2Weight, setYear3Sem2Weight] = useState('50');
  
  const [year1sem1, setYear1Sem1] = useState([emptyModule(), emptyModule(), emptyModule()]);
  const [year1sem2, setYear1Sem2] = useState([emptyModule(), emptyModule(), emptyModule()]);
  const [year2sem1, setYear2Sem1] = useState([emptyModule(), emptyModule(), emptyModule()]);
  const [year2sem2, setYear2Sem2] = useState([emptyModule(), emptyModule(), emptyModule()]);
  const [year3sem1, setYear3Sem1] = useState([emptyModule(), emptyModule(), emptyModule()]);
  const [year3sem2, setYear3Sem2] = useState([emptyModule(), emptyModule(), emptyModule()]);

  // State for collapsible sections - load from localStorage, default all closed
  const [year1Collapsed, setYear1Collapsed] = useState(() => {
    const saved = localStorage.getItem('year1Collapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [year2Collapsed, setYear2Collapsed] = useState(() => {
    const saved = localStorage.getItem('year2Collapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [year3Collapsed, setYear3Collapsed] = useState(() => {
    const saved = localStorage.getItem('year3Collapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Save collapsed states to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('year1Collapsed', JSON.stringify(year1Collapsed));
  }, [year1Collapsed]);

  useEffect(() => {
    localStorage.setItem('year2Collapsed', JSON.stringify(year2Collapsed));
  }, [year2Collapsed]);

  useEffect(() => {
    localStorage.setItem('year3Collapsed', JSON.stringify(year3Collapsed));
  }, [year3Collapsed]);

  const clearForm = () => {
    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      setTargetScore('70');
      setYear1Weight('0');
      setYear2Weight('40');
      setYear3Weight('60');
      setYear1Sem1Weight('50');
      setYear1Sem2Weight('50');
      setYear2Sem1Weight('50');
      setYear2Sem2Weight('50');
      setYear3Sem1Weight('50');
      setYear3Sem2Weight('50');
      setYear1Sem1([emptyModule(), emptyModule(), emptyModule()]);
      setYear1Sem2([emptyModule(), emptyModule(), emptyModule()]);
      setYear2Sem1([emptyModule(), emptyModule(), emptyModule()]);
      setYear2Sem2([emptyModule(), emptyModule(), emptyModule()]);
      setYear3Sem1([emptyModule(), emptyModule(), emptyModule()]);
      setYear3Sem2([emptyModule(), emptyModule(), emptyModule()]);
      setYear1Collapsed(true);
      setYear2Collapsed(true);
      setYear3Collapsed(true);
      localStorage.removeItem('gradeGoalData');
    }
  };

  // Handle Enter key to move to next input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const form = e.target.form || e.target.closest('div');
      if (form) {
        const inputs = Array.from(document.querySelectorAll('input:not([type="button"])'));
        const index = inputs.indexOf(e.target);
        if (index > -1 && index < inputs.length - 1) {
          inputs[index + 1].focus();
        }
      }
    }
  };

  // Load data from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('gradeGoalData');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        
        // Migration function: convert old 'weight' to new 'credits'
        const migrateModule = (module) => {
          if (!module.credits && module.weight) {
            // Old format: module.weight was percentage (0-100)
            // New format: module.credits should be credits (0-60 per semester)
            // Convert: if weight was 33.33%, that's ~20 credits (33.33% of 60 = 20)
            const weight = parseFloat(module.weight) || 0;
            const credits = Math.round((weight / 100) * 60);
            return { ...module, credits: credits.toString(), weight: undefined };
          }
          return module;
        };
        
        const migrateSemester = (semester) => {
          return semester.map(migrateModule);
        };
        
        setTargetScore(data.targetScore || '70');
        setYear1Weight(data.year1Weight || '0');
        setYear2Weight(data.year2Weight || '40');
        setYear3Weight(data.year3Weight || '60');
        setYear1Sem1Weight(data.year1sem1Weight || '50');
        setYear1Sem2Weight(data.year1sem2Weight || '50');
        setYear2Sem1Weight(data.year2sem1Weight || '50');
        setYear2Sem2Weight(data.year2sem2Weight || '50');
        setYear3Sem1Weight(data.year3sem1Weight || '50');
        setYear3Sem2Weight(data.year3sem2Weight || '50');
        setYear1Sem1(migrateSemester(data.year1sem1 || [emptyModule(), emptyModule(), emptyModule()]));
        setYear1Sem2(migrateSemester(data.year1sem2 || [emptyModule(), emptyModule(), emptyModule()]));
        setYear2Sem1(migrateSemester(data.year2sem1 || [emptyModule(), emptyModule(), emptyModule()]));
        setYear2Sem2(migrateSemester(data.year2sem2 || [emptyModule(), emptyModule(), emptyModule()]));
        setYear3Sem1(migrateSemester(data.year3sem1 || [emptyModule(), emptyModule(), emptyModule()]));
        setYear3Sem2(migrateSemester(data.year3sem2 || [emptyModule(), emptyModule(), emptyModule()]));
      } catch (e) {
        console.error('Error loading saved data:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save data to localStorage whenever it changes
  useEffect(() => {
    const data = {
      targetScore,
      year1Weight,
      year2Weight,
      year3Weight,
      year1sem1Weight,
      year1sem2Weight,
      year2sem1Weight,
      year2sem2Weight,
      year3sem1Weight,
      year3sem2Weight,
      year1sem1,
      year1sem2,
      year2sem1,
      year2sem2,
      year3sem1,
      year3sem2
    };
    localStorage.setItem('gradeGoalData', JSON.stringify(data));
  }, [targetScore, year1Weight, year2Weight, year3Weight, 
      year1sem1Weight, year1sem2Weight, year2sem1Weight, year2sem2Weight, 
      year3sem1Weight, year3sem2Weight, year1sem1, year1sem2, year2sem1, year2sem2, year3sem1, year3sem2]);

  const updateModule = (setter, modules, moduleIdx, field, value) => {
    const newModules = [...modules];
    newModules[moduleIdx][field] = value;
    setter(newModules);
  };

  const updateAssessment = (setter, modules, moduleIdx, assessmentIdx, field, value) => {
    const newModules = [...modules];
    newModules[moduleIdx].assessments[assessmentIdx][field] = value;
    setter(newModules);
  };

  const addModule = (setter, modules) => {
    setter([...modules, emptyModule()]);
  };

  const removeModule = (setter, modules, moduleIdx) => {
    if (modules.length > 1) {
      setter(modules.filter((_, idx) => idx !== moduleIdx));
    }
  };

  const addAssessment = (setter, modules, moduleIdx) => {
    const newModules = [...modules];
    newModules[moduleIdx].assessments.push(emptyAssessment());
    setter(newModules);
  };

  const removeAssessment = (setter, modules, moduleIdx, assessmentIdx) => {
    const newModules = [...modules];
    if (newModules[moduleIdx].assessments.length > 1) {
      newModules[moduleIdx].assessments = newModules[moduleIdx].assessments.filter((_, idx) => idx !== assessmentIdx);
      setter(newModules);
    }
  };

  const calculateModuleScore = (module) => {
    let totalWeight = 0;
    let weightedScore = 0;
    
    module.assessments.forEach(assessment => {
      const weight = parseFloat(assessment.weight) || 0;
      const score = parseFloat(assessment.score) || 0;
      totalWeight += weight;
      weightedScore += (score * weight / 100);
    });
    
    return totalWeight > 0 ? weightedScore / totalWeight * 100 : 0;
  };

  const getAssessmentWeightTotal = (module) => {
    return module.assessments.reduce((sum, assessment) => sum + (parseFloat(assessment.weight) || 0), 0);
  };

  const calculateSemesterScore = (modules) => {
    let totalCredits = 0;
    let weightedScore = 0;
    
    // Calculate total credits entered
    const enteredCredits = modules.reduce((sum, m) => sum + (parseFloat(m.credits) || 0), 0);
    
    modules.forEach(module => {
      let moduleCredits = parseFloat(module.credits) || 0;
      
      // If no credits entered anywhere, assume equal distribution (60 credits per semester)
      if (enteredCredits === 0 && modules.length > 0) {
        moduleCredits = 60 / modules.length;
      }
      
      const moduleScore = calculateModuleScore(module);
      const moduleWeight = (moduleCredits / 60) * 100; // Convert credits to % (60 per semester)
      totalCredits += moduleCredits;
      weightedScore += (moduleScore * moduleWeight / 100);
    });
    
    return totalCredits > 0 ? weightedScore : 0;
  };

  const getModuleCreditsTotal = (modules) => {
    return modules.reduce((sum, module) => sum + (parseFloat(module.credits) || 0), 0);
  };

  const calculateYearScore = (sem1Modules, sem2Modules, sem1Weight, sem2Weight) => {
    const sem1Score = calculateSemesterScore(sem1Modules);
    const sem2Score = calculateSemesterScore(sem2Modules);
    const w1 = parseFloat(sem1Weight) || 0;
    const w2 = parseFloat(sem2Weight) || 0;
    
    if (w1 + w2 === 0) return 0;
    return (sem1Score * w1 + sem2Score * w2) / (w1 + w2);
  };

  const getSemesterWeightTotal = (sem1Weight, sem2Weight) => {
    return (parseFloat(sem1Weight) || 0) + (parseFloat(sem2Weight) || 0);
  };

  const getYearWeightTotal = () => {
    return (parseFloat(year1Weight) || 0) + (parseFloat(year2Weight) || 0) + (parseFloat(year3Weight) || 0);
  };

  const calculateOverallScore = () => {
    const year1Score = calculateYearScore(year1sem1, year1sem2, year1sem1Weight, year1sem2Weight);
    const year2Score = calculateYearScore(year2sem1, year2sem2, year2sem1Weight, year2sem2Weight);
    const year3Score = calculateYearScore(year3sem1, year3sem2, year3sem1Weight, year3sem2Weight);
    const w1 = parseFloat(year1Weight) || 0;
    const w2 = parseFloat(year2Weight) || 0;
    const w3 = parseFloat(year3Weight) || 0;
    
    if (w1 + w2 + w3 === 0) return 0;
    return (year1Score * w1 + year2Score * w2 + year3Score * w3) / (w1 + w2 + w3);
  };

  const calculateRequiredScore = () => {
    const target = parseFloat(targetScore) || 0;
    const y1w = parseFloat(year1Weight) || 0;
    const y2w = parseFloat(year2Weight) || 0;
    const y3w = parseFloat(year3Weight) || 0;
    
    const getModuleStats = (module) => {
      let filledWeight = 0;
      let unfilledWeight = 0;
      let filledWeightedScore = 0;
      
      module.assessments.forEach(assessment => {
        const weight = parseFloat(assessment.weight) || 0;
        const score = parseFloat(assessment.score);
        
        if (score !== '' && !isNaN(score)) {
          filledWeight += weight;
          filledWeightedScore += (score * weight / 100);
        } else {
          unfilledWeight += weight;
        }
      });
      
      return { filledWeight, unfilledWeight, filledWeightedScore };
    };
    
    let totalFilledContribution = 0;
    let totalUnfilledWeight = 0;
    
    const allSemesters = [
      { modules: year1sem1, semWeight: parseFloat(year1sem1Weight) || 0, yearWeight: y1w },
      { modules: year1sem2, semWeight: parseFloat(year1sem2Weight) || 0, yearWeight: y1w },
      { modules: year2sem1, semWeight: parseFloat(year2sem1Weight) || 0, yearWeight: y2w },
      { modules: year2sem2, semWeight: parseFloat(year2sem2Weight) || 0, yearWeight: y2w },
      { modules: year3sem1, semWeight: parseFloat(year3sem1Weight) || 0, yearWeight: y3w },
      { modules: year3sem2, semWeight: parseFloat(year3sem2Weight) || 0, yearWeight: y3w }
    ];
    
    allSemesters.forEach(sem => {
      sem.modules.forEach(module => {
        const moduleCredits = parseFloat(module.credits) || 0;
        const moduleWeight = (moduleCredits / 60) * 100;  // Convert credits to percentage of semester
        const stats = getModuleStats(module);
        
        if (moduleCredits > 0) {
          const filledContribution = stats.filledWeightedScore * (moduleWeight / 100) * (sem.semWeight / 100) * (sem.yearWeight / 100);
          totalFilledContribution += filledContribution;
          
          const unfilledWeightContribution = (stats.unfilledWeight / 100) * (moduleWeight / 100) * (sem.semWeight / 100) * (sem.yearWeight / 100);
          totalUnfilledWeight += unfilledWeightContribution;
        }
      });
    });
    
    if (totalUnfilledWeight === 0) {
      return null;
    }
    
    const requiredAvg = (target - totalFilledContribution) / totalUnfilledWeight;
    return requiredAvg;
  };

  // Simple function to show required score - just use the overall calculation
  const getDisplayRequiredScore = () => {
    const required = calculateRequiredScore();
    if (required === null || required < 0) return null;
    return required;
  };

  // For module - check if it has blanks, calculate what they need to get THIS MODULE to target
  const getModuleRequiredScore = (module) => {
    let hasEmpty = false;
    let filledScore = 0;
    let unfilledWeight = 0;
    let totalWeight = 0;
    
    module.assessments.forEach(assessment => {
      const weight = parseFloat(assessment.weight) || 0;
      const score = parseFloat(assessment.score);
      totalWeight += weight;
      
      if (score !== '' && !isNaN(score)) {
        filledScore += (score * weight / 100);
      } else {
        unfilledWeight += weight;
        hasEmpty = true;
      }
    });
    
    // Don't show if weights aren't valid (close to 100%) - avoids Infinity and unrealistic numbers
    if (!hasEmpty || Math.abs(totalWeight - 100) >= 0.5) return null;
    
    const target = parseFloat(targetScore) || 0;
    const required = ((target - filledScore) / unfilledWeight) * 100;
    
    if (required < 0 || !isFinite(required)) return null;
    return required;
  };

  // For semester - check if it has blanks, calculate what they need to get THIS SEMESTER to target
  const getSemesterRequiredScore = (modules) => {
    let hasEmpty = false;
    let hasData = false;  // Track if there's any actual data entered
    let semesterFilledScore = 0;
    let semesterUnfilledWeight = 0;
    let totalModuleCredits = 0;
    
    modules.forEach(module => {
      const moduleCredits = parseFloat(module.credits) || 0;
      totalModuleCredits += moduleCredits;
      const moduleWeight = (moduleCredits / 60) * 100;  // Convert credits to percentage
      let moduleFilledScore = 0;
      let moduleUnfilledWeight = 0;
      
      module.assessments.forEach(assessment => {
        const weight = parseFloat(assessment.weight) || 0;
        const score = parseFloat(assessment.score);
        
        if (weight > 0) hasData = true;  // There's actual data if any assessment has weight
        
        if (score !== '' && !isNaN(score)) {
          moduleFilledScore += (score * weight / 100);
        } else {
          moduleUnfilledWeight += weight;
          hasEmpty = true;
        }
      });
      
      // Module's contribution to semester score
      semesterFilledScore += moduleFilledScore * (moduleWeight / 100);
      semesterUnfilledWeight += (moduleUnfilledWeight / 100) * (moduleWeight / 100);
    });
    
    // Don't show if module credits aren't valid, no blanks, or no data entered
    if (!hasEmpty || !hasData || Math.abs(totalModuleCredits - 60) >= 0.5) return null;
    
    const target = parseFloat(targetScore) || 0;
    const required = (target - semesterFilledScore) / semesterUnfilledWeight;
    
    if (required < 0 || !isFinite(required)) return null;
    return required;
  };

  // For year - check if it has blanks, calculate what they need to get THIS YEAR to target
  const getYearRequiredScore = (sem1Modules, sem2Modules, sem1Weight, sem2Weight) => {
    let hasEmpty = false;
    let hasData = false;  // Track if there's any actual data entered
    let yearFilledScore = 0;
    let yearUnfilledWeight = 0;
    
    const processSemester = (modules, semWeight) => {
      let semesterFilledScore = 0;
      let semesterUnfilledWeight = 0;
      
      modules.forEach(module => {
        const moduleCredits = parseFloat(module.credits) || 0;
        const moduleWeight = (moduleCredits / 60) * 100;  // Convert credits to percentage
        let moduleFilledScore = 0;
        let moduleUnfilledWeight = 0;
        
        module.assessments.forEach(assessment => {
          const weight = parseFloat(assessment.weight) || 0;
          const score = parseFloat(assessment.score);
          
          if (weight > 0) hasData = true;  // There's actual data if any assessment has weight
          
          if (score !== '' && !isNaN(score)) {
            moduleFilledScore += (score * weight / 100);
          } else {
            moduleUnfilledWeight += weight;
            hasEmpty = true;
          }
        });
        
        semesterFilledScore += moduleFilledScore * (moduleWeight / 100);
        semesterUnfilledWeight += (moduleUnfilledWeight / 100) * (moduleWeight / 100);
      });
      
      const semWeightProp = parseFloat(semWeight) || 0;
      yearFilledScore += semesterFilledScore * (semWeightProp / 100);
      yearUnfilledWeight += semesterUnfilledWeight * (semWeightProp / 100);
    };
    
    processSemester(sem1Modules, sem1Weight);
    processSemester(sem2Modules, sem2Weight);
    
    // Don't show if no empty fields OR no data entered at all
    if (!hasEmpty || !hasData) return null;
    
    const target = parseFloat(targetScore) || 0;
    const required = (target - yearFilledScore) / yearUnfilledWeight;
    
    if (required < 0 || !isFinite(required)) return null;
    return required;
  };

  const renderModule = (module, moduleIdx, modules, setter, semesterName, semWeight, yearWeight) => {
    const moduleScore = calculateModuleScore(module);
    const assessmentWeightTotal = getAssessmentWeightTotal(module);
    const isAssessmentWeightValid = Math.abs(assessmentWeightTotal - 100) < 0.5;
    const requiredScore = getModuleRequiredScore(module);

    return (
      <div key={moduleIdx} className="border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50">
        <div className="flex gap-1 sm:gap-2 items-center mb-2">
          <input
            type="text"
            placeholder="Module name"
            value={module.name}
            onChange={(e) => updateModule(setter, modules, moduleIdx, 'name', e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 min-w-0 px-2 py-1 border rounded text-sm"
          />
          <div className="flex items-center gap-1">
            <input
              type="number"
              placeholder="Credits"
              value={module.credits}
              onChange={(e) => updateModule(setter, modules, moduleIdx, 'credits', e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-14 sm:w-20 px-1 sm:px-2 py-1 border rounded text-sm"
              min="0"
              max="120"
            />
            <span className="text-xs text-gray-600">credits</span>
          </div>
          <button
            onClick={() => removeModule(setter, modules, moduleIdx)}
            className="p-1 text-gray-500 hover:bg-gray-100 rounded flex-shrink-0"
            title="Remove module"
          >
            <Trash2 size={16} />
          </button>
        </div>

        <div className="space-y-1 ml-4">
          {module.assessments.map((assessment, assessmentIdx) => (
            <div key={assessmentIdx} className="flex gap-1 sm:gap-2 items-center">
              <input
                type="text"
                placeholder="Assessment name"
                value={assessment.name}
                onChange={(e) => updateAssessment(setter, modules, moduleIdx, assessmentIdx, 'name', e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 min-w-0 px-2 py-1 border rounded text-xs bg-white"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  placeholder="%"
                  value={assessment.weight}
                  onChange={(e) => updateAssessment(setter, modules, moduleIdx, assessmentIdx, 'weight', e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-12 sm:w-16 px-1 sm:px-2 py-1 border rounded text-xs bg-white"
                  min="0"
                  max="100"
                />
                <span className="text-xs text-gray-600">%</span>
              </div>
              <input
                type="number"
                placeholder="Score"
                value={assessment.score}
                onChange={(e) => updateAssessment(setter, modules, moduleIdx, assessmentIdx, 'score', e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-14 sm:w-20 px-1 sm:px-2 py-1 border rounded text-xs bg-white"
                min="0"
                max="100"
              />
              <button
                onClick={() => removeAssessment(setter, modules, moduleIdx, assessmentIdx)}
                className="p-1 text-gray-500 hover:bg-gray-100 rounded flex-shrink-0"
                title="Remove assessment"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            onClick={() => addAssessment(setter, modules, moduleIdx)}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded"
          >
            <Plus size={14} /> Add Assessment
          </button>
        </div>

        <div className="mt-2 text-right">
          <div className="text-xs font-medium text-blue-600">
            Module Score: {moduleScore.toFixed(1)}%
          </div>
          {requiredScore !== null && isAssessmentWeightValid && (
            <div className="text-xs font-medium text-green-600">
              {requiredScore > 100 ? (
                <span className="text-red-600">Target no longer achievable for this module</span>
              ) : (
                <>Need {(() => {
                  // If within 0.2 of target, show target exactly to avoid floating point display issues
                  if (Math.abs(requiredScore - target) < 0.2) return target.toFixed(1);
                  return (Math.ceil(requiredScore * 10) / 10).toFixed(1);
                })()}% average in remaining assessments</>
              )}
            </div>
          )}
          {!isAssessmentWeightValid && module.assessments.length > 0 && assessmentWeightTotal > 0 && (
            <div className="text-xs font-medium text-red-600">
              ⚠ Assessment weights: {assessmentWeightTotal.toFixed(1)}% (should be 100%)
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSemester = (modules, setter, semesterName, semWeight, yearWeight, semWeightSetter) => {
    const semScore = calculateSemesterScore(modules);
    const moduleCreditsTotal = getModuleCreditsTotal(modules);
    
    // Check if all modules have valid assessment weights
    const allModulesHaveValidWeights = modules.every(module => {
      const assessmentWeightTotal = getAssessmentWeightTotal(module);
      return Math.abs(assessmentWeightTotal - 100) < 0.5;
    });
    
    const requiredScore = getSemesterRequiredScore(modules);

    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-sm sm:text-base text-gray-700">{semesterName}</h4>
          <div className="flex items-center gap-1 sm:gap-2">
            <input
              type="number"
              value={semWeight}
              onChange={(e) => semWeightSetter(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-12 sm:w-16 px-1 sm:px-2 py-1 border rounded-md text-sm text-center"
              min="0"
              max="100"
            />
            <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">% of year</span>
          </div>
        </div>
        {modules.map((module, idx) => renderModule(module, idx, modules, setter, semesterName, semWeight, yearWeight))}
        <button
          onClick={() => addModule(setter, modules)}
          className="flex items-center gap-1 px-3 py-1 text-sm text-gray-500 hover:bg-gray-100 rounded mb-2"
        >
          <Plus size={16} /> Add Module
        </button>
        <div className="text-right">
          <div className="text-sm font-medium text-blue-600">
            Semester Score: {semScore.toFixed(1)}%
          </div>
          {requiredScore !== null && allModulesHaveValidWeights && (
            <div className="text-sm font-medium text-green-600">
              {requiredScore > 100 ? (
                <span className="text-red-600">Target no longer achievable for this semester</span>
              ) : (
                <>Need {(() => {
                  if (Math.abs(requiredScore - target) < 0.2) return target.toFixed(1);
                  return (Math.ceil(requiredScore * 10) / 10).toFixed(1);
                })()}% average in remaining assessments</>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const year2Score = calculateYearScore(year2sem1, year2sem2, year2sem1Weight, year2sem2Weight);
  const year3Score = calculateYearScore(year3sem1, year3sem2, year3sem1Weight, year3sem2Weight);
  const overallScore = calculateOverallScore();
  const target = parseFloat(targetScore) || 0;
  const difference = overallScore - target;
  const requiredScore = getDisplayRequiredScore();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 md:p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg p-3 md:p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex flex-col items-start gap-2">
              <img src={logo} alt="GradeGoal" className="h-20 sm:h-30" style={{maxHeight: '120px'}} />
              <div className="flex items-center gap-2">
                <p className="text-xs sm:text-sm text-gray-600">Calculate the required scores to achieve your target degree classification</p>
                <div className="relative group">
                  <Info size={16} className="text-indigo-600 cursor-help flex-shrink-0" />
                  <div className="absolute left-0 top-6 w-72 sm:w-80 bg-white border border-indigo-200 rounded-lg shadow-lg p-4 text-xs text-gray-700 invisible group-hover:visible z-10 max-w-[calc(100vw-2rem)]">
                    <p className="font-semibold mb-2 text-indigo-600">How to use GradeGoal:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Set your <strong>target percentage</strong> (e.g., 70% for First Class)</li>
                      <li>Set <strong>year weights</strong> (typically: Year 1 = 0%, Year 2 = 40%, Year 3 = 60%)</li>
                      <li>For each year, set <strong>semester weights</strong> (usually 50% / 50%)</li>
                      <li>Enter your <strong>modules</strong> with credits (e.g., 20 credits per module, 120 per year)</li>
                      <li>Add <strong>assessments</strong> with their weights (must total 100% per module)</li>
                      <li>Fill in <strong>scores</strong> for completed assessments</li>
                      <li>The calculator shows what you need in remaining assessments to hit your target!</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={clearForm}
              className="px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-md border border-gray-300 shadow-sm whitespace-nowrap"
            >
              <span className="hidden sm:inline">Clear All Data</span>
              <span className="sm:hidden">Clear</span>
            </button>
          </div>
          
          {/* UK Degree Classification Table */}
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-200">
            <h3 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">UK Degree Classifications</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <div className="bg-white rounded px-3 py-2 text-center border border-indigo-100">
                <div className="font-bold text-indigo-600">First Class</div>
                <div className="text-gray-600">70% +</div>
              </div>
              <div className="bg-white rounded px-3 py-2 text-center border border-indigo-100">
                <div className="font-bold text-indigo-600">Upper Second (2:1)</div>
                <div className="text-gray-600">60-69%</div>
              </div>
              <div className="bg-white rounded px-3 py-2 text-center border border-indigo-100">
                <div className="font-bold text-indigo-600">Lower Second (2:2)</div>
                <div className="text-gray-600">50-59%</div>
              </div>
              <div className="bg-white rounded px-3 py-2 text-center border border-indigo-100">
                <div className="font-bold text-indigo-600">Third Class</div>
                <div className="text-gray-600">40-49%</div>
              </div>
              <div className="bg-white rounded px-3 py-2 text-center border border-indigo-100">
                <div className="font-bold text-indigo-600">Fail</div>
                <div className="text-gray-600">&lt;40%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Target and Year Weights */}
        <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Overall %</label>
              <input
                type="number"
                value={targetScore}
                onChange={(e) => setTargetScore(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-indigo-100 rounded-md"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year 1 Weight %</label>
              <input
                type="number"
                value={year1Weight}
                onChange={(e) => setYear1Weight(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-indigo-100 rounded-md"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year 2 Weight %</label>
              <input
                type="number"
                value={year2Weight}
                onChange={(e) => setYear2Weight(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-indigo-100 rounded-md"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year 3 Weight %</label>
              <input
                type="number"
                value={year3Weight}
                onChange={(e) => setYear3Weight(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-indigo-100 rounded-md"
                min="0"
                max="100"
              />
            </div>
          </div>
          {Math.abs(getYearWeightTotal() - 100) >= 0.5 && (
            <div className="mt-3 text-sm font-medium text-red-600">
              ⚠ Year weights total: {getYearWeightTotal()}% (should be 100%)
            </div>
          )}
        </div>

        {/* Year 1 */}
        <div className="border border-gray-300 rounded-lg p-2 sm:p-4 mb-6 overflow-hidden">
          <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setYear1Collapsed(!year1Collapsed)}>
            <h2 className="text-2xl font-bold text-gray-800">Year 1</h2>
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              {year1Collapsed ? <ChevronDown size={24} className="text-gray-600" /> : <ChevronUp size={24} className="text-gray-600" />}
            </button>
          </div>
          
          {!year1Collapsed && (
            <>
              {Math.abs(getSemesterWeightTotal(year1sem1Weight, year1sem2Weight) - 100) >= 0.5 && (
                <div className="mb-3 text-sm font-medium text-red-600">
                  ⚠ Semester weights total: {getSemesterWeightTotal(year1sem1Weight, year1sem2Weight)}% (should be 100%)
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>{renderSemester(year1sem1, setYear1Sem1, 'Semester 1', year1sem1Weight, year1Weight, setYear1Sem1Weight)}</div>
                <div>{renderSemester(year1sem2, setYear1Sem2, 'Semester 2', year1sem2Weight, year1Weight, setYear1Sem2Weight)}</div>
              </div>
              
              {(() => {
                const getModuleCreditsTotal = (modules) => modules.reduce((sum, m) => sum + (parseFloat(m.credits) || 0), 0);
                const year1TotalCredits = getModuleCreditsTotal(year1sem1) + getModuleCreditsTotal(year1sem2);
                const isYearCreditsValid = Math.abs(year1TotalCredits - 120) < 0.5;
                
                return !isYearCreditsValid && year1TotalCredits > 0 && (
                  <div className="mb-3 text-sm font-medium text-red-600">
                    ⚠ Year credits: {year1TotalCredits.toFixed(0)} (should be 120)
                  </div>
                );
              })()}
            </>
          )}
          
          <div className="text-right text-lg font-bold text-indigo-600 mt-2">
            Year 1 Score: {calculateYearScore(year1sem1, year1sem2, year1sem1Weight, year1sem2Weight).toFixed(1)}%
          </div>
          {(() => {
            const yearRequired = getYearRequiredScore(year1sem1, year1sem2, year1sem1Weight, year1sem2Weight);
            const isSemesterWeightValid = Math.abs(getSemesterWeightTotal(year1sem1Weight, year1sem2Weight) - 100) < 0.5;
            // Check if year has valid total credits (120)
            const getModuleCreditsTotal = (modules) => modules.reduce((sum, m) => sum + (parseFloat(m.credits) || 0), 0);
            const year1TotalCredits = getModuleCreditsTotal(year1sem1) + getModuleCreditsTotal(year1sem2);
            const areYearCreditsValid = year1sem1.length === 0 || year1sem2.length === 0 || Math.abs(year1TotalCredits - 120) < 0.5;
            
            return yearRequired !== null && isSemesterWeightValid && areYearCreditsValid && (
              <div className="text-right text-sm font-medium text-green-600">
                {yearRequired > 100 ? (
                  <span className="text-red-600">Target no longer achievable for this year</span>
                ) : (
                  <>Need {(() => {
                    if (Math.abs(yearRequired - target) < 0.2) return target.toFixed(1);
                    return (Math.ceil(yearRequired * 10) / 10).toFixed(1);
                  })()}% average in remaining assessments</>
                )}
              </div>
            );
          })()}
        </div>

        {/* Year 2 */}
        <div className="border border-gray-300 rounded-lg p-2 sm:p-4 mb-6 overflow-hidden">
          <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setYear2Collapsed(!year2Collapsed)}>
            <h2 className="text-2xl font-bold text-gray-800">Year 2</h2>
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              {year2Collapsed ? <ChevronDown size={24} className="text-gray-600" /> : <ChevronUp size={24} className="text-gray-600" />}
            </button>
          </div>
          
          {!year2Collapsed && (
            <>
              {Math.abs(getSemesterWeightTotal(year2sem1Weight, year2sem2Weight) - 100) >= 0.5 && (
                <div className="mb-3 text-sm font-medium text-red-600">
                  ⚠ Semester weights total: {getSemesterWeightTotal(year2sem1Weight, year2sem2Weight)}% (should be 100%)
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>{renderSemester(year2sem1, setYear2Sem1, 'Semester 1', year2sem1Weight, year2Weight, setYear2Sem1Weight)}</div>
                <div>{renderSemester(year2sem2, setYear2Sem2, 'Semester 2', year2sem2Weight, year2Weight, setYear2Sem2Weight)}</div>
              </div>
              
              {(() => {
                const getModuleCreditsTotal = (modules) => modules.reduce((sum, m) => sum + (parseFloat(m.credits) || 0), 0);
                const year2TotalCredits = getModuleCreditsTotal(year2sem1) + getModuleCreditsTotal(year2sem2);
                const isYearCreditsValid = Math.abs(year2TotalCredits - 120) < 0.5;
                
                return !isYearCreditsValid && year2TotalCredits > 0 && (
                  <div className="mb-3 text-sm font-medium text-red-600">
                    ⚠ Year credits: {year2TotalCredits.toFixed(0)} (should be 120)
                  </div>
                );
              })()}
            </>
          )}
          
          <div className="text-right text-lg font-bold text-indigo-600 mt-2">
            Year 2 Score: {year2Score.toFixed(1)}%
          </div>
          {(() => {
            const yearRequired = getYearRequiredScore(year2sem1, year2sem2, year2sem1Weight, year2sem2Weight);
            const isSemesterWeightValid = Math.abs(getSemesterWeightTotal(year2sem1Weight, year2sem2Weight) - 100) < 0.5;
            // Check if year has valid total credits (120)
            const getModuleCreditsTotal = (modules) => modules.reduce((sum, m) => sum + (parseFloat(m.credits) || 0), 0);
            const year2TotalCredits = getModuleCreditsTotal(year2sem1) + getModuleCreditsTotal(year2sem2);
            const areYearCreditsValid = year2sem1.length === 0 || year2sem2.length === 0 || Math.abs(year2TotalCredits - 120) < 0.5;
            
            return yearRequired !== null && isSemesterWeightValid && areYearCreditsValid && (
              <div className="text-right text-sm font-medium text-green-600">
                {yearRequired > 100 ? (
                  <span className="text-red-600">Target no longer achievable for this year</span>
                ) : (
                  <>Need {(() => {
                    if (Math.abs(yearRequired - target) < 0.2) return target.toFixed(1);
                    return (Math.ceil(yearRequired * 10) / 10).toFixed(1);
                  })()}% average in remaining assessments</>
                )}
              </div>
            );
          })()}
        </div>

        {/* Year 3 */}
        <div className="border border-gray-300 rounded-lg p-2 sm:p-4 mb-6 overflow-hidden">
          <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setYear3Collapsed(!year3Collapsed)}>
            <h2 className="text-2xl font-bold text-gray-800">Year 3</h2>
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              {year3Collapsed ? <ChevronDown size={24} className="text-gray-600" /> : <ChevronUp size={24} className="text-gray-600" />}
            </button>
          </div>
          
          {!year3Collapsed && (
            <>
              {Math.abs(getSemesterWeightTotal(year3sem1Weight, year3sem2Weight) - 100) >= 0.5 && (
                <div className="mb-3 text-sm font-medium text-red-600">
                  ⚠ Semester weights total: {getSemesterWeightTotal(year3sem1Weight, year3sem2Weight)}% (should be 100%)
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>{renderSemester(year3sem1, setYear3Sem1, 'Semester 1', year3sem1Weight, year3Weight, setYear3Sem1Weight)}</div>
                <div>{renderSemester(year3sem2, setYear3Sem2, 'Semester 2', year3sem2Weight, year3Weight, setYear3Sem2Weight)}</div>
              </div>
              
              {(() => {
                const getModuleCreditsTotal = (modules) => modules.reduce((sum, m) => sum + (parseFloat(m.credits) || 0), 0);
                const year3TotalCredits = getModuleCreditsTotal(year3sem1) + getModuleCreditsTotal(year3sem2);
                const isYearCreditsValid = Math.abs(year3TotalCredits - 120) < 0.5;
                
                return !isYearCreditsValid && year3TotalCredits > 0 && (
                  <div className="mb-3 text-sm font-medium text-red-600">
                    ⚠ Year credits: {year3TotalCredits.toFixed(0)} (should be 120)
                  </div>
                );
              })()}
            </>
          )}
          
          <div className="text-right text-lg font-bold text-indigo-600 mt-2">
            Year 3 Score: {year3Score.toFixed(1)}%
          </div>
          {(() => {
            const yearRequired = getYearRequiredScore(year3sem1, year3sem2, year3sem1Weight, year3sem2Weight);
            const isSemesterWeightValid = Math.abs(getSemesterWeightTotal(year3sem1Weight, year3sem2Weight) - 100) < 0.5;
            // Check if year has valid total credits (120)
            const getModuleCreditsTotal = (modules) => modules.reduce((sum, m) => sum + (parseFloat(m.credits) || 0), 0);
            const year3TotalCredits = getModuleCreditsTotal(year3sem1) + getModuleCreditsTotal(year3sem2);
            const areYearCreditsValid = year3sem1.length === 0 || year3sem2.length === 0 || Math.abs(year3TotalCredits - 120) < 0.5;
            
            return yearRequired !== null && isSemesterWeightValid && areYearCreditsValid && (
              <div className="text-right text-sm font-medium text-green-600">
                {yearRequired > 100 ? (
                  <span className="text-red-600">Target no longer achievable for this year</span>
                ) : (
                  <>Need {(() => {
                    if (Math.abs(yearRequired - target) < 0.2) return target.toFixed(1);
                    return (Math.ceil(yearRequired * 10) / 10).toFixed(1);
                  })()}% average in remaining assessments</>
                )}
              </div>
            );
          })()}
        </div>

        {/* Overall Results */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-lg">
          <h2 className="text-2xl font-bold mb-4">Overall Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-sm opacity-90">Current Overall</div>
              <div className="text-3xl font-bold">{overallScore.toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-sm opacity-90">Target</div>
              <div className="text-3xl font-bold">{target}%</div>
            </div>
            <div>
              <div className="text-sm opacity-90">Difference</div>
              <div className={`text-3xl font-bold ${difference >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                {difference >= 0 ? '+' : ''}{difference.toFixed(1)}%
              </div>
            </div>
          </div>
          {(() => {
            // Don't show overall required score unless both years have basic structure
            const year2SemWeightValid = Math.abs(getSemesterWeightTotal(year2sem1Weight, year2sem2Weight) - 100) < 0.5;
            const year3SemWeightValid = Math.abs(getSemesterWeightTotal(year3sem1Weight, year3sem2Weight) - 100) < 0.5;
            const yearWeightValid = Math.abs(getYearWeightTotal() - 100) < 0.5;
            
            // Basic structure needed: year weights and semester weights for both years
            const hasBasicStructure = yearWeightValid && year2SemWeightValid && year3SemWeightValid;
            
            if (!requiredScore || !hasBasicStructure) return null;
            
            return (
              <div className="text-center mt-4">
                <div className="text-lg font-medium text-yellow-300">
                  {(() => {
                    // Additional checks for "not achievable" message
                    const getModuleCreditsTotal = (modules) => modules.reduce((sum, m) => sum + (parseFloat(m.credits) || 0), 0);
                    const year1TotalCredits = getModuleCreditsTotal(year1sem1) + getModuleCreditsTotal(year1sem2);
                    const year2TotalCredits = getModuleCreditsTotal(year2sem1) + getModuleCreditsTotal(year2sem2);
                    const year3TotalCredits = getModuleCreditsTotal(year3sem1) + getModuleCreditsTotal(year3sem2);
                    
                    const year1CreditsValid = year1sem1.length === 0 || year1sem2.length === 0 || Math.abs(year1TotalCredits - 120) < 0.5;
                    const year2CreditsValid = year2sem1.length === 0 || year2sem2.length === 0 || Math.abs(year2TotalCredits - 120) < 0.5;
                    const year3CreditsValid = year3sem1.length === 0 || year3sem2.length === 0 || Math.abs(year3TotalCredits - 120) < 0.5;
                    
                    const hasData = overallScore > 0;
                    const structureComplete = year1CreditsValid && year2CreditsValid && year3CreditsValid && hasData;
                    
                    if (requiredScore > 100 && structureComplete) {
                      return <span className="text-red-300">Target no longer achievable overall</span>;
                    }
                    return <>Need {(() => {
                      if (Math.abs(requiredScore - target) < 0.2) return target.toFixed(1);
                      return (Math.ceil(requiredScore * 10) / 10).toFixed(1);
                    })()}% average in all remaining assessments</>;
                  })()}
                </div>
              </div>
            );
          })()}
        </div>
        
        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-600">
          <p>
            Have feedback or questions? <a href="mailto:feedback.gradegoal@gmail.com?subject=GradeGoal%20Feedback" className="text-indigo-600 hover:text-indigo-800 underline">Contact us</a>
          </p>
        </div>
      </div>
    </div>
  );
}
